import { Dialog, DialogRef, DIALOG_DATA } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TagPusherService, TagEvent } from '@libs/shared-services';
import { ffdNotesRoutePrefix } from '@libs/utils';
import { ConfirmDialogComponent, ConfirmDialogData, ConfirmDialogResult } from '@libs/ui-lib';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaaAuthorizedSignerDetailsComponent } from './baa-authorized-signer-details';
import { BaaSignFormComponent } from './baa-sign-form';
import { BaaRequestSentSuccessComponent } from './baa-request-sent-success';
import { BaaSignSuccessComponent } from './baa-sign-success';
import { BaaSignModalStore } from './baa-sign-modal.store';
import { BaaSignModalData, BaaSignModalStep } from './baa-sign-modal.types';

export { BaaSignModalData, BaaSignModalStep } from './baa-sign-modal.types';

@Component({
  selector: 'lib-baa-sign-modal',
  standalone: true,
  imports: [
    CommonModule,
    SvgIconComponent,
    TranslateModule,
    BaaSignFormComponent,
    BaaSignSuccessComponent,
    BaaAuthorizedSignerDetailsComponent,
    BaaRequestSentSuccessComponent,
  ],
  templateUrl: './baa-sign-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaaSignModalComponent implements OnInit {
  private readonly dialogRef = inject(DialogRef);
  private readonly dialog = inject(Dialog);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly tagPusherService = inject(TagPusherService);
  private readonly dialogData = inject<BaaSignModalData>(DIALOG_DATA);

  protected readonly modalStore = inject(BaaSignModalStore);

  @ViewChild(BaaSignFormComponent) private readonly formComponent?: BaaSignFormComponent;
  @ViewChild(BaaAuthorizedSignerDetailsComponent) private readonly signerDetailsComponent?: BaaAuthorizedSignerDetailsComponent;

  protected readonly BaaSignModalStep = BaaSignModalStep;

  constructor() {
    effect(() => {
      const step = this.modalStore.step();
      if (step === BaaSignModalStep.AuthorizedSuccess) {
        this.tagPusherService.pushTag({ event: TagEvent.FfdNotesBaaSigned, location: this.dialogData.location });
      }
      if (step === BaaSignModalStep.NotAuthorizedSuccess) {
        this.tagPusherService.pushTag({ event: TagEvent.FfdNotesBaaSignRequest, location: this.dialogData.location });
      }
    });
  }

  public ngOnInit(): void {
    if (!this.dialogData.authorizedOption) {
      console.error('BaaSignModal opened without authorizedOption');
      this.dialogRef.close();
      return;
    }
    const step =
      this.dialogData.authorizedOption === 'authorized'
        ? BaaSignModalStep.AuthorizedAgreement
        : BaaSignModalStep.NotAuthorizedSignerDetails;
    this.modalStore.setInitialStep(step);
  }

  protected onCloseDialog(): void {
    const step = this.modalStore.step();
    if (step === BaaSignModalStep.AuthorizedSuccess || step === BaaSignModalStep.NotAuthorizedSuccess) {
      this.dialogRef.close();
      this.router.navigateByUrl(ffdNotesRoutePrefix().join('/'));
      return;
    }
    if (this.formComponent?.isDirty || this.signerDetailsComponent?.isDirty) {
      this.openExitConfirmation();
    } else {
      this.tagPusherService.pushTag({ event: TagEvent.FfdNotesBaaAborted, location: this.dialogData.location });
      this.dialogRef.close();
    }
  }

  protected onReturnToNotes(): void {
    this.dialogRef.close();
    this.router.navigateByUrl(ffdNotesRoutePrefix().join('/'));
  }

  private openExitConfirmation(): void {
    const data: ConfirmDialogData = {
      title: this.translateService.instant('express_app_baa_exit_modal_title'),
      description: this.translateService.instant('express_app_baa_exit_modal_description'),
      cancelLabel: this.translateService.instant('express_app_baa_exit_modal_return'),
      confirmLabel: this.translateService.instant('express_app_baa_exit_modal_exit'),
      destructive: true,
    };

    const confirmRef = this.dialog.open<ConfirmDialogResult>(ConfirmDialogComponent, {
      data,
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      maxWidth: '392px',
    });

    confirmRef.closed.subscribe((result) => {
      if (result?.confirm) {
        this.tagPusherService.pushTag({ event: TagEvent.FfdNotesBaaAborted, location: this.dialogData.location });
        this.dialogRef.close();
      }
    });
  }
}
